import type { MessageKey } from "../../shared/i18n";

/** Where the current view was opened from; carried in `location.state`. */
export type BackOrigin = "search" | "scan";

/**
 * `location.state` contract for the back link: opening a result or a scanned
 * container records the origin so the back target is deterministic even after
 * a reload-free history walk (spec §5–6).
 */
export interface BackState {
  from: BackOrigin;
  /** The search query string to restore (leading `?`), for `from: "search"`. */
  search?: string;
}

export interface BackLocation {
  pathname: string;
  search: string;
  state: unknown;
}

export interface BackContext {
  location: BackLocation;
  /** Router history index; 0 means the view is a deep link (no in-app history). */
  historyIndex: number;
  /** Parent container id of a browse node; null at the root. */
  parentOf?: (containerId: string) => string | null | undefined;
  /** Owning container of an item; null when the item has no placement. */
  owningContainerOf?: (itemId: string) => string | null | undefined;
}

export type BackTarget =
  | { kind: "path"; to: string; labelKey: MessageKey }
  | { kind: "history"; labelKey: MessageKey };

/** Build the `location.state` an entry opened from the search results carries. */
export function searchBackState(search: string): BackState {
  return { from: "search", search };
}

/**
 * Read the current in-app history index from the browser history state.
 * React Router v7 keeps it in `history.state.idx`; a deep link starts at 0.
 *
 * @returns the number of history entries behind the current one.
 */
export function readHistoryIndex(): number {
  if (typeof window === "undefined") return 0;
  const state = window.history.state as { idx?: number | null } | null;
  return typeof state?.idx === "number" ? state.idx : 0;
}

/**
 * Resolve the back target and its label for the current view (spec §6).
 *
 * Order matters: an explicit origin (`from: "search" | "scan"`) wins, then the
 * structural routes (edit, browse node, item detail), then in-app history, and
 * a deep link without history always lands on storage.
 *
 * @param context current location, history depth and the lookups the route needs.
 * @returns either a concrete path or "go back in history", plus the label key.
 */
export function resolveBackTarget(context: BackContext): BackTarget {
  const { location, historyIndex } = context;
  const state = asBackState(location.state);
  if (state?.from === "search") {
    return { kind: "path", to: `/search${searchQueryOf(state.search)}`, labelKey: "backToSearch" };
  }
  if (state?.from === "scan") {
    return { kind: "path", to: "/scan", labelKey: "backToScan" };
  }

  const segments = location.pathname.split("/").filter((segment) => segment.length > 0);
  const [head, id] = segments;
  if (head && id && segments.length === 2) {
    if (head === "edit") {
      return { kind: "path", to: `/browse/${id}`, labelKey: "back" };
    }
    if (head === "browse") {
      const parentId = context.parentOf?.(id);
      return { kind: "path", to: parentId ? `/browse/${parentId}` : "/browse", labelKey: "back" };
    }
    if (head === "items") {
      const containerId = context.owningContainerOf?.(id);
      if (containerId) return { kind: "path", to: `/browse/${containerId}`, labelKey: "back" };
    }
  }

  if (historyIndex > 0) return { kind: "history", labelKey: "back" };
  return { kind: "path", to: "/browse", labelKey: "backToStorage" };
}

/** Narrow `location.state` to the back contract; anything else is ignored. */
function asBackState(state: unknown): BackState | null {
  if (typeof state !== "object" || state === null) return null;
  const candidate = state as { from?: unknown; search?: unknown };
  if (candidate.from !== "search" && candidate.from !== "scan") return null;
  return {
    from: candidate.from,
    search: typeof candidate.search === "string" ? candidate.search : undefined,
  };
}

/** Only a real query string is trusted as a search target. */
function searchQueryOf(search: string | undefined): string {
  return search && search.startsWith("?") ? search : "";
}
